/**
 * ReceitasTable Component
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Pencil, Trash2, FileDown } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { PermissionGate } from "@/components/PermissionGate";
import type { Receita } from "@/types/receita";

interface ReceitasTableProps {
  receitas: Receita[];
  isLoading: boolean;
  onEdit: (receita: Receita) => void;
  onView: (receita: Receita) => void;
  onDelete: (receita: Receita) => void;
  onPrint: (receita: Receita) => void;
}

const dificuldadeLabels = {
  facil: "Fácil",
  media: "Média",
  dificil: "Difícil",
};

const dificuldadeColors = {
  facil: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  media: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  dificil: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

export function ReceitasTable({ receitas, isLoading, onEdit, onView, onDelete, onPrint }: ReceitasTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (receitas.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-muted-foreground">Nenhuma receita encontrada.</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Dificuldade</TableHead>
            <TableHead className="text-right">Custo Total</TableHead>
            <TableHead>Data Criação</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {receitas.map((receita) => (
            <TableRow key={receita.id_receita}>
              <TableCell className="font-medium">{receita.nome}</TableCell>
              <TableCell>{receita.categoria || "—"}</TableCell>
              <TableCell>
                {receita.dificuldade ? (
                  <Badge variant="outline" className={dificuldadeColors[receita.dificuldade]}>
                    {dificuldadeLabels[receita.dificuldade]}
                  </Badge>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(receita.custo_total)}
              </TableCell>
              <TableCell>
                {format(new Date(receita.created_at), "dd/MM/yyyy", { locale: ptBR })}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" onClick={() => onView(receita)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onPrint(receita)}>
                    <FileDown className="h-4 w-4" />
                  </Button>
                  <PermissionGate resource="receitas" action="edit">
                    <Button variant="ghost" size="icon" onClick={() => onEdit(receita)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </PermissionGate>
                  <PermissionGate resource="receitas" action="delete">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(receita)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </PermissionGate>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
